import { useId, useState } from "react";
import {
  BRANDING, OCCASIONS, QTY_BANDS, QUESTION_TOPICS, RECIPIENTS,
  type BrandingId, type OccasionId, type QtyBandId, type QuestionTopicId, type RecipientId, type TimingId,
} from "../ids";
import type { ComposerApi } from "../useRequestComposer";
import { BoxPicker } from "./BoxPicker";
import { ChipGroup } from "./Choices";
import { Glyph, IntentIcon } from "./Icons";

/**
 * Passo 2 — «Qualche dettaglio»: solo domande strutturate, tutte facoltative,
 * diverse per argomento. Le risposte di un altro argomento restano nella bozza
 * (tornando indietro ricompaiono) ma non partono.
 */
export function DetailsStep({ api }: { api: ComposerApi }) {
  const uid = useId();
  const { t, state, steps } = api;
  const d = state.draft;
  const titleId = `${uid}-t`;
  const n = Math.max(1, steps.indexOf(2) + 1);
  const intent = d.intent;

  return (
    <section data-step="2" hidden={state.step !== 2} aria-labelledby={titleId} className="rq-section">
      {intent && (
        <div className="rq-chosen">
          <span className="rq-chosen__what">
            <IntentIcon id={intent} size={22} />
            {t(`contact.guide.intents.${intent}.label`)}
          </span>
          <button type="button" className="rq-link" onClick={() => api.goTo(1)}>{t("contact.guide.nav.changeTopic")}</button>
        </div>
      )}
      <div className="rq-step__headrow">
        <h3 id={titleId} ref={api.setEl("h2")} tabIndex={-1} data-rq-focus-target className="rq-step__title">
          <span className="rq-sr">{t("contact.guide.a11y.stepPrefix", { n, total: steps.length })} </span>
          {t("contact.guide.step2.title")}
        </h3>
        {/* Nella pagina il passo può essere lungo (otto card): si salta senza scorrere fino in fondo. */}
        {api.opts.surface === "page" && (
          <button type="button" className="rq-link" onClick={api.next} data-gb="salta">
            {t("contact.guide.nav.skip")}
            <Glyph name="next" size={12} />
          </button>
        )}
      </div>
      <p className="rq-step__hint">{t("contact.guide.step2.hint")}</p>

      <ContextHint api={api} />

      {intent === "offer" && (
        <>
          <BoxPicker api={api} mode="qty" legend={t("contact.guide.q.boxes")} />
          {api.showOfferQtyBand && <QtyBandQuestion api={api} />}
          <TimingQuestion api={api} />
          <BrandingAddon api={api} />
        </>
      )}
      {intent === "custom" && (
        <>
          <BoxPicker api={api} mode="multi" legend={t("contact.guide.q.boxesCustom")} />
          <QtyBandQuestion api={api} />
          <TimingQuestion api={api} />
          <BrandingAddon api={api} />
        </>
      )}
      {intent === "branding" && (
        <>
          <BrandingQuestion api={api} />
          <BoxPicker api={api} mode="multi" legend={t("contact.guide.q.boxesBranding")} />
          <QtyBandQuestion api={api} />
          <TimingQuestion api={api} />
        </>
      )}
      {intent === "advice" && <AdviceQuestions api={api} />}
      {intent === "question" && (
        <>
          <BoxPicker api={api} mode="single" legend={t("contact.guide.q.box")} />
          <ChipGroup
            legend={t("contact.guide.q.questionTopics")}
            optional={t("contact.guide.optional")}
            multiple
            options={QUESTION_TOPICS.map((q) => ({ value: q, label: t(`contact.guide.questionTopic.${q}`) }))}
            value={d.questionTopics}
            onChange={(v, on) => api.dispatch({ type: "toggleQuestionTopic", id: v as QuestionTopicId, on })}
          />
        </>
      )}
    </section>
  );
}

/**
 * Aiuto contestuale: la confezione della scheda prodotto o quelle del carrello
 * sono già scelte. Le confezioni che il catalogo non mostra (carrello vecchio,
 * catalogo non arrivato) non hanno una card da deselezionare: qui si tolgono.
 */
function ContextHint({ api }: { api: ComposerApi }) {
  const { t, state, stamps, catalog, dispatch } = api;
  const d = state.draft;
  const shown = catalog.data?.bySlug ?? null;
  const loose = stamps.filter((s) => d.boxes.includes(s.slug) && !(shown && Object.prototype.hasOwnProperty.call(shown, s.slug)));
  const text = state.contextHint === "cart" && stamps.length
    ? t("contact.guide.hints.boxesFromCart")
    : state.contextHint === "page" && stamps.length
      ? t("contact.guide.hints.boxesFromPage", { box: stamps[0].title })
      : null;
  if (!text && !loose.length) return null;
  return (
    <div className="rq-callout rq-group" data-gb="aiuto-contesto">
      <Glyph name="info" size={16} />
      <div className="rq-callout__body">
        {text && <span>{text}</span>}
        {loose.length > 0 && (
          <ul className="rq-loose">
            {loose.map((s) => (
              <li key={s.slug}>
                <span>{s.title}</span>
                <button
                  type="button"
                  className="rq-link"
                  aria-label={t("contact.guide.hints.removeAria", { box: s.title })}
                  onClick={() => dispatch({ type: "toggleBox", slug: s.slug, on: false })}
                >
                  {t("contact.guide.hints.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function QtyBandQuestion({ api }: { api: ComposerApi }) {
  const { t, state, dispatch } = api;
  return (
    <ChipGroup
      legend={t("contact.guide.q.qtyBand")}
      optional={t("contact.guide.optional")}
      options={QTY_BANDS.map((q) => ({ value: q, label: t(`contact.guide.qtyBand.${q}`) }))}
      value={state.draft.qtyBand ? [state.draft.qtyBand] : []}
      onChange={(v) => dispatch({ type: "qtyBand", id: v as QtyBandId })}
    />
  );
}

function TimingQuestion({ api }: { api: ComposerApi }) {
  const uid = useId();
  const { t, state, dispatch, season, todayIso, errors } = api;
  const d = state.draft;
  const ids: TimingId[] = [...(season.christmas ? (["christmas"] as const) : []), "asap", "date", "exploring"];
  const dateId = `${uid}-date`;
  const errId = `${uid}-err`;
  return (
    <>
      <ChipGroup
        legend={t("contact.guide.q.timing")}
        optional={t("contact.guide.optional")}
        options={ids.map((id) => ({
          value: id,
          label: id === "christmas" ? t("contact.guide.timing.christmas", { year: season.year }) : t(`contact.guide.timing.${id}`),
        }))}
        value={d.timing ? [d.timing] : []}
        onChange={(v) => dispatch({ type: "timing", id: v as TimingId })}
      />
      {d.timing === "date" && (
        <div className="rq-field rq-reveal">
          <label htmlFor={dateId} className="rq-label">{t("contact.guide.timing.dateLabel")}</label>
          <input
            id={dateId}
            ref={api.setEl("date")}
            className="rq-input"
            type="date"
            min={todayIso}
            value={d.date}
            aria-invalid={errors.date || undefined}
            aria-describedby={errors.date ? errId : undefined}
            onChange={(e) => dispatch({ type: "date", iso: e.currentTarget.value })}
          />
          {errors.date && (
            <p id={errId} className="rq-err"><Glyph name="alert" size={14} />{t("contact.guide.errDate")}</p>
          )}
        </div>
      )}
    </>
  );
}

function BrandingQuestion({ api, legendKey = "contact.guide.q.branding" }: { api: ComposerApi; legendKey?: string }) {
  const { t, state, dispatch } = api;
  return (
    <ChipGroup
      legend={t(legendKey)}
      optional={t("contact.guide.optional")}
      multiple
      options={BRANDING.map((b) => ({ value: b, label: t(`contact.guide.branding.${b}`) }))}
      value={state.draft.branding}
      onChange={(v, on) => dispatch({ type: "toggleBranding", id: v as BrandingId, on })}
    />
  );
}

/** Nell'offerta e nella box su misura la personalizzazione è un'aggiunta che si apre a richiesta. */
function BrandingAddon({ api }: { api: ComposerApi }) {
  const uid = useId();
  const { t, state } = api;
  const [open, setOpen] = useState(() => state.draft.branding.length > 0);
  const panelId = `${uid}-p`;
  return (
    <div className="rq-group">
      <button type="button" className="rq-disclosure" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        <span className="rq-disclosure__icon" aria-hidden="true"><Glyph name={open ? "minus" : "plus"} size={14} /></span>
        {t("contact.guide.q.brandingAddon")}
        <span className="rq-legend__opt">{t("contact.guide.optional")}</span>
      </button>
      <div id={panelId} hidden={!open} className="rq-reveal">
        <BrandingQuestion api={api} />
      </div>
    </div>
  );
}

function AdviceQuestions({ api }: { api: ComposerApi }) {
  const { t, state, dispatch } = api;
  const d = state.draft;
  return (
    <>
      <ChipGroup
        legend={t("contact.guide.q.occasion")}
        optional={t("contact.guide.optional")}
        options={OCCASIONS.map((o) => ({ value: o, label: t(`contact.guide.occasion.${o}`) }))}
        value={d.occasion ? [d.occasion] : []}
        onChange={(v) => dispatch({ type: "occasion", id: v as OccasionId })}
      />
      <ChipGroup
        legend={t("contact.guide.q.recipients")}
        optional={t("contact.guide.optional")}
        multiple
        options={RECIPIENTS.map((r) => ({ value: r, label: t(`contact.guide.recipients.${r}`) }))}
        value={d.recipients}
        onChange={(v, on) => dispatch({ type: "toggleRecipient", id: v as RecipientId, on })}
      />
      <QtyBandQuestion api={api} />
      <TimingQuestion api={api} />
      {/* Solo abbinamenti già pubblicati sul sito: nessun consiglio inventato. */}
      <div className="rq-callout rq-group">
        <Glyph name="info" size={16} />
        <div className="rq-callout__body">
          <strong>{t("contact.guide.guide.title")}</strong>
          <ul className="rq-guide">
            <li>{t("contact.guide.guide.small")}</li>
            <li data-hl={d.occasion === "clients" || d.recipients.includes("clients") ? "" : undefined}>{t("contact.guide.guide.clients")}</li>
            <li data-hl={d.occasion === "recurring" || d.recipients.includes("teams") ? "" : undefined}>{t("contact.guide.guide.team")}</li>
          </ul>
        </div>
      </div>
    </>
  );
}
